// The words of the image viewer and of media-ref states (DES1-specs "images across Apps,
// Artifacts, exports and Chat"; DES2-image-ux.md).
//
// Namespace `media`; owner: DES2 (ui/workspace/media/). The alt text and the source name come
// from the data; these are only the words around them. Placeholders: {n}, {total}, {alt},
// {date}, {source}. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  viewer: 'Image viewer',
  close: 'Close',
  previous: 'Previous image',
  next: 'Next image',
  counter: '{n} of {total}',
  announce: 'Image {n} of {total}, {alt}',
  loading: 'Loading image',
  missing: 'No image',
  expired: 'Image no longer available',
  expiredSeen: 'Image no longer available · seen {date}',
  refused: 'Image couldn’t be used',
  provenance: '{source} · as seen {date}',
  changedSince: 'The image has changed since then.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Image pending',
};

export type MediaCopyKey = keyof typeof en;

const sv: Record<MediaCopyKey, string> = {
  viewer: 'Bildvisare',
  close: 'Stäng',
  previous: 'Föregående bild',
  next: 'Nästa bild',
  counter: '{n} av {total}',
  announce: 'Bild {n} av {total}, {alt}',
  loading: 'Läser in bild',
  missing: 'Ingen bild',
  expired: 'Bilden finns inte längre',
  expiredSeen: 'Bilden finns inte längre · sågs {date}',
  refused: 'Bilden kunde inte användas',
  provenance: '{source} · som den såg ut {date}',
  changedSince: 'Bilden har ändrats sedan dess.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Bilden väntar',
};

const bg: Record<MediaCopyKey, string> = {
  viewer: 'Преглед на изображения',
  close: 'Затвори',
  previous: 'Предишно изображение',
  next: 'Следващо изображение',
  counter: '{n} от {total}',
  announce: 'Изображение {n} от {total}, {alt}',
  loading: 'Изображението се зарежда',
  missing: 'Няма изображение',
  expired: 'Изображението вече не е налично',
  expiredSeen: 'Изображението вече не е налично · видяно {date}',
  refused: 'Изображението не можа да се използва',
  provenance: '{source} · както е видяно {date}',
  changedSince: 'Изображението е променено оттогава.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Изображението предстои',
};

const cs: Record<MediaCopyKey, string> = {
  viewer: 'Prohlížeč obrázků',
  close: 'Zavřít',
  previous: 'Předchozí obrázek',
  next: 'Další obrázek',
  counter: '{n} z {total}',
  announce: 'Obrázek {n} z {total}, {alt}',
  loading: 'Načítání obrázku',
  missing: 'Žádný obrázek',
  expired: 'Obrázek už není k dispozici',
  expiredSeen: 'Obrázek už není k dispozici · viděno {date}',
  refused: 'Obrázek nešlo použít',
  provenance: '{source} · jak vypadal {date}',
  changedSince: 'Obrázek se od té doby změnil.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Obrázek se připravuje',
};

const da: Record<MediaCopyKey, string> = {
  viewer: 'Billedfremviser',
  close: 'Luk',
  previous: 'Forrige billede',
  next: 'Næste billede',
  counter: '{n} af {total}',
  announce: 'Billede {n} af {total}, {alt}',
  loading: 'Indlæser billede',
  missing: 'Intet billede',
  expired: 'Billedet er ikke længere tilgængeligt',
  expiredSeen: 'Billedet er ikke længere tilgængeligt · set {date}',
  refused: 'Billedet kunne ikke bruges',
  provenance: '{source} · som set {date}',
  changedSince: 'Billedet er ændret siden da.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Billedet afventer',
};

const de: Record<MediaCopyKey, string> = {
  viewer: 'Bildanzeige',
  close: 'Schließen',
  previous: 'Vorheriges Bild',
  next: 'Nächstes Bild',
  counter: '{n} von {total}',
  announce: 'Bild {n} von {total}, {alt}',
  loading: 'Bild wird geladen',
  missing: 'Kein Bild',
  expired: 'Bild nicht mehr verfügbar',
  expiredSeen: 'Bild nicht mehr verfügbar · gesehen am {date}',
  refused: 'Bild konnte nicht verwendet werden',
  provenance: '{source} · Stand {date}',
  changedSince: 'Das Bild hat sich seitdem geändert.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Bild ausstehend',
};

const el: Record<MediaCopyKey, string> = {
  viewer: 'Προβολή εικόνων',
  close: 'Κλείσιμο',
  previous: 'Προηγούμενη εικόνα',
  next: 'Επόμενη εικόνα',
  counter: '{n} από {total}',
  announce: 'Εικόνα {n} από {total}, {alt}',
  loading: 'Φόρτωση εικόνας',
  missing: 'Δεν υπάρχει εικόνα',
  expired: 'Η εικόνα δεν είναι πλέον διαθέσιμη',
  expiredSeen: 'Η εικόνα δεν είναι πλέον διαθέσιμη · εμφανίστηκε {date}',
  refused: 'Δεν ήταν δυνατή η χρήση της εικόνας',
  provenance: '{source} · όπως εμφανίστηκε {date}',
  changedSince: 'Η εικόνα έχει αλλάξει από τότε.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Η εικόνα εκκρεμεί',
};

const es: Record<MediaCopyKey, string> = {
  viewer: 'Visor de imágenes',
  close: 'Cerrar',
  previous: 'Imagen anterior',
  next: 'Imagen siguiente',
  counter: '{n} de {total}',
  announce: 'Imagen {n} de {total}, {alt}',
  loading: 'Cargando imagen',
  missing: 'Sin imagen',
  expired: 'La imagen ya no está disponible',
  expiredSeen: 'La imagen ya no está disponible · vista el {date}',
  refused: 'No se pudo usar la imagen',
  provenance: '{source} · tal como se vio el {date}',
  changedSince: 'La imagen ha cambiado desde entonces.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Imagen pendiente',
};

const et: Record<MediaCopyKey, string> = {
  viewer: 'Pildivaatur',
  close: 'Sulge',
  previous: 'Eelmine pilt',
  next: 'Järgmine pilt',
  counter: '{n} / {total}',
  announce: 'Pilt {n} / {total}, {alt}',
  loading: 'Pildi laadimine',
  missing: 'Pilt puudub',
  expired: 'Pilt pole enam saadaval',
  expiredSeen: 'Pilt pole enam saadaval · nähtud {date}',
  refused: 'Pilti ei saanud kasutada',
  provenance: '{source} · nähtud {date}',
  changedSince: 'Pilt on vahepeal muutunud.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Pilt on ootel',
};

const fi: Record<MediaCopyKey, string> = {
  viewer: 'Kuvien katselu',
  close: 'Sulje',
  previous: 'Edellinen kuva',
  next: 'Seuraava kuva',
  counter: '{n}/{total}',
  announce: 'Kuva {n}/{total}, {alt}',
  loading: 'Ladataan kuvaa',
  missing: 'Ei kuvaa',
  expired: 'Kuva ei ole enää saatavilla',
  expiredSeen: 'Kuva ei ole enää saatavilla · nähty {date}',
  refused: 'Kuvaa ei voitu käyttää',
  provenance: '{source} · nähty {date}',
  changedSince: 'Kuva on muuttunut sen jälkeen.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Kuva odottaa',
};

const fr: Record<MediaCopyKey, string> = {
  viewer: 'Visionneuse d’images',
  close: 'Fermer',
  previous: 'Image précédente',
  next: 'Image suivante',
  counter: '{n} sur {total}',
  announce: 'Image {n} sur {total}, {alt}',
  loading: 'Chargement de l’image',
  missing: 'Aucune image',
  expired: 'Image plus disponible',
  expiredSeen: 'Image plus disponible · vue le {date}',
  refused: 'Impossible d’utiliser l’image',
  provenance: '{source} · telle que vue le {date}',
  changedSince: 'L’image a changé depuis.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Image en attente',
};

const ga: Record<MediaCopyKey, string> = {
  viewer: 'Amharcán íomhánna',
  close: 'Dún',
  previous: 'An íomhá roimhe',
  next: 'An chéad íomhá eile',
  counter: '{n} as {total}',
  announce: 'Íomhá {n} as {total}, {alt}',
  loading: 'Íomhá á lódáil',
  missing: 'Níl aon íomhá ann',
  expired: 'Níl an íomhá ar fáil a thuilleadh',
  expiredSeen: 'Níl an íomhá ar fáil a thuilleadh · feicthe {date}',
  refused: 'Níorbh fhéidir an íomhá a úsáid',
  provenance: '{source} · mar a chonacthas {date}',
  changedSince: 'Tá an íomhá athraithe ó shin.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Íomhá ar feitheamh',
};

const hr: Record<MediaCopyKey, string> = {
  viewer: 'Preglednik slika',
  close: 'Zatvori',
  previous: 'Prethodna slika',
  next: 'Sljedeća slika',
  counter: '{n} od {total}',
  announce: 'Slika {n} od {total}, {alt}',
  loading: 'Učitavanje slike',
  missing: 'Nema slike',
  expired: 'Slika više nije dostupna',
  expiredSeen: 'Slika više nije dostupna · viđeno {date}',
  refused: 'Sliku nije bilo moguće upotrijebiti',
  provenance: '{source} · kako je viđeno {date}',
  changedSince: 'Slika se u međuvremenu promijenila.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Slika je na čekanju',
};

const hu: Record<MediaCopyKey, string> = {
  viewer: 'Képnézegető',
  close: 'Bezárás',
  previous: 'Előző kép',
  next: 'Következő kép',
  counter: '{n}/{total}',
  announce: '{n}/{total}. kép, {alt}',
  loading: 'Kép betöltése',
  missing: 'Nincs kép',
  expired: 'A kép már nem érhető el',
  expiredSeen: 'A kép már nem érhető el · látva: {date}',
  refused: 'A képet nem lehetett használni',
  provenance: '{source} · ahogy {date} látszott',
  changedSince: 'A kép azóta megváltozott.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'A kép függőben',
};

const it: Record<MediaCopyKey, string> = {
  viewer: 'Visualizzatore di immagini',
  close: 'Chiudi',
  previous: 'Immagine precedente',
  next: 'Immagine successiva',
  counter: '{n} di {total}',
  announce: 'Immagine {n} di {total}, {alt}',
  loading: 'Caricamento immagine',
  missing: 'Nessuna immagine',
  expired: 'Immagine non più disponibile',
  expiredSeen: 'Immagine non più disponibile · vista il {date}',
  refused: 'Impossibile usare l’immagine',
  provenance: '{source} · come visto il {date}',
  changedSince: 'L’immagine è cambiata da allora.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Immagine in attesa',
};

const lt: Record<MediaCopyKey, string> = {
  viewer: 'Vaizdų peržiūra',
  close: 'Uždaryti',
  previous: 'Ankstesnis vaizdas',
  next: 'Kitas vaizdas',
  counter: '{n} iš {total}',
  announce: 'Vaizdas {n} iš {total}, {alt}',
  loading: 'Įkeliamas vaizdas',
  missing: 'Vaizdo nėra',
  expired: 'Vaizdas nebepasiekiamas',
  expiredSeen: 'Vaizdas nebepasiekiamas · matytas {date}',
  refused: 'Vaizdo nepavyko panaudoti',
  provenance: '{source} · kaip matyta {date}',
  changedSince: 'Vaizdas nuo to laiko pasikeitė.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Vaizdas laukia',
};

const lv: Record<MediaCopyKey, string> = {
  viewer: 'Attēlu skatītājs',
  close: 'Aizvērt',
  previous: 'Iepriekšējais attēls',
  next: 'Nākamais attēls',
  counter: '{n} no {total}',
  announce: 'Attēls {n} no {total}, {alt}',
  loading: 'Ielādē attēlu',
  missing: 'Nav attēla',
  expired: 'Attēls vairs nav pieejams',
  expiredSeen: 'Attēls vairs nav pieejams · redzēts {date}',
  refused: 'Attēlu nevarēja izmantot',
  provenance: '{source} · kā redzēts {date}',
  changedSince: 'Attēls kopš tā laika ir mainījies.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Attēls gaida',
};

const mt: Record<MediaCopyKey, string> = {
  viewer: 'Wiri tal-istampi',
  close: 'Agħlaq',
  previous: 'Stampa ta’ qabel',
  next: 'Stampa li jmiss',
  counter: '{n} minn {total}',
  announce: 'Stampa {n} minn {total}, {alt}',
  loading: 'Qed tittella’ l-istampa',
  missing: 'L-ebda stampa',
  expired: 'L-istampa m’għadhiex disponibbli',
  expiredSeen: 'L-istampa m’għadhiex disponibbli · dehret {date}',
  refused: 'L-istampa ma setgħetx tintuża',
  provenance: '{source} · kif dehret {date}',
  changedSince: 'L-istampa nbidlet minn dak iż-żmien.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'L-istampa pendenti',
};

const nl: Record<MediaCopyKey, string> = {
  viewer: 'Afbeeldingsviewer',
  close: 'Sluiten',
  previous: 'Vorige afbeelding',
  next: 'Volgende afbeelding',
  counter: '{n} van {total}',
  announce: 'Afbeelding {n} van {total}, {alt}',
  loading: 'Afbeelding laden',
  missing: 'Geen afbeelding',
  expired: 'Afbeelding niet meer beschikbaar',
  expiredSeen: 'Afbeelding niet meer beschikbaar · gezien op {date}',
  refused: 'Afbeelding kon niet worden gebruikt',
  provenance: '{source} · zoals gezien op {date}',
  changedSince: 'De afbeelding is sindsdien gewijzigd.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Afbeelding in afwachting',
};

const pl: Record<MediaCopyKey, string> = {
  viewer: 'Przeglądarka obrazów',
  close: 'Zamknij',
  previous: 'Poprzedni obraz',
  next: 'Następny obraz',
  counter: '{n} z {total}',
  announce: 'Obraz {n} z {total}, {alt}',
  loading: 'Wczytywanie obrazu',
  missing: 'Brak obrazu',
  expired: 'Obraz nie jest już dostępny',
  expiredSeen: 'Obraz nie jest już dostępny · widziany {date}',
  refused: 'Nie można było użyć obrazu',
  provenance: '{source} · stan z {date}',
  changedSince: 'Obraz zmienił się od tego czasu.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Obraz oczekuje',
};

const pt: Record<MediaCopyKey, string> = {
  viewer: 'Visualizador de imagens',
  close: 'Fechar',
  previous: 'Imagem anterior',
  next: 'Imagem seguinte',
  counter: '{n} de {total}',
  announce: 'Imagem {n} de {total}, {alt}',
  loading: 'A carregar imagem',
  missing: 'Sem imagem',
  expired: 'Imagem já não disponível',
  expiredSeen: 'Imagem já não disponível · vista em {date}',
  refused: 'Não foi possível usar a imagem',
  provenance: '{source} · tal como vista em {date}',
  changedSince: 'A imagem mudou desde então.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Imagem pendente',
};

const ro: Record<MediaCopyKey, string> = {
  viewer: 'Vizualizator de imagini',
  close: 'Închide',
  previous: 'Imaginea anterioară',
  next: 'Imaginea următoare',
  counter: '{n} din {total}',
  announce: 'Imaginea {n} din {total}, {alt}',
  loading: 'Se încarcă imaginea',
  missing: 'Nicio imagine',
  expired: 'Imaginea nu mai este disponibilă',
  expiredSeen: 'Imaginea nu mai este disponibilă · văzută pe {date}',
  refused: 'Imaginea nu a putut fi folosită',
  provenance: '{source} · așa cum a fost văzută pe {date}',
  changedSince: 'Imaginea s-a schimbat de atunci.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Imagine în așteptare',
};

const sk: Record<MediaCopyKey, string> = {
  viewer: 'Prehliadač obrázkov',
  close: 'Zavrieť',
  previous: 'Predchádzajúci obrázok',
  next: 'Ďalší obrázok',
  counter: '{n} z {total}',
  announce: 'Obrázok {n} z {total}, {alt}',
  loading: 'Načítava sa obrázok',
  missing: 'Žiadny obrázok',
  expired: 'Obrázok už nie je k dispozícii',
  expiredSeen: 'Obrázok už nie je k dispozícii · videné {date}',
  refused: 'Obrázok sa nedal použiť',
  provenance: '{source} · ako vyzeral {date}',
  changedSince: 'Obrázok sa odvtedy zmenil.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Obrázok čaká',
};

const sl: Record<MediaCopyKey, string> = {
  viewer: 'Pregledovalnik slik',
  close: 'Zapri',
  previous: 'Prejšnja slika',
  next: 'Naslednja slika',
  counter: '{n} od {total}',
  announce: 'Slika {n} od {total}, {alt}',
  loading: 'Nalaganje slike',
  missing: 'Ni slike',
  expired: 'Slika ni več na voljo',
  expiredSeen: 'Slika ni več na voljo · videno {date}',
  refused: 'Slike ni bilo mogoče uporabiti',
  provenance: '{source} · kot je bilo videti {date}',
  changedSince: 'Slika se je medtem spremenila.',
  // The capturing state in exports (T1-EXP consumes this key; no EXP dictionary).
  mediaPending: 'Slika je v pripravi',
};

export const mediaCopy: NamespaceCatalog<MediaCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default mediaCopy;
